import {computed, observable, runInAction} from 'mobx';
import {TechseeBaseController} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-controller';
import {ChangeSettingsModalController} from '../../modals/change-settings-modal/ChangeSettingsModal.controller';
import {ChangeAccountModalController} from '../../modals/change-account-modal/ChangeAccountModal.controller';
import {ChangeSettingsModalState} from '../../modals/change-settings-modal/ChangeSettingsModal.state';
import {ChangeAccountModalState} from '../../modals/change-account-modal/ChangeAccountModal.state';
import {ITsAppUserService} from '../../services/TsAppUserService';
import {IAuthService} from '../../services/auth/auth.service';
import {AppState} from '../../app.state';
import {IRedirectionService} from '../../services/RedirectionService';

export interface IConnectivityControllerDependencies {
    accountData: any;
    currentUser: any;
    tsStateHelper: any;
    tsUrlUtils: any;
    redirectionService: IRedirectionService;
    tsAppUserService: ITsAppUserService;
    authService: IAuthService;
    appState: AppState;
    displayTabletAsDesktop: boolean;
}

export class ConnectivityController extends TechseeBaseController {
    readonly accountData: any;

    readonly currentUser: any;

    private readonly stateHelper: any;

    private readonly redirectionService: IRedirectionService;

    private readonly tsAppUserService: ITsAppUserService;

    private readonly authService: IAuthService;

    private readonly appState: AppState;

    private readonly displayTabletAsDesktop: boolean;

    @observable
    changeSettingsModalController?: ChangeSettingsModalController;

    @observable
    changeAccountModalController?: ChangeAccountModalController;

    constructor(dependencies: IConnectivityControllerDependencies) {
        super();
        this.accountData = dependencies.accountData;
        this.currentUser = dependencies.currentUser;
        this.stateHelper = dependencies.tsStateHelper;
        this.redirectionService = dependencies.redirectionService;
        this.tsAppUserService = dependencies.tsAppUserService;
        this.authService = dependencies.authService;
        this.appState = dependencies.appState;
        this.displayTabletAsDesktop = dependencies.displayTabletAsDesktop;
    }

    @computed
    get isMobileDevice(): boolean {
        return this.env.isMobile(this.displayTabletAsDesktop);
    }

    @computed
    get isIOS(): boolean {
        return this.env.isIOS();
    }

    private _getChangeSettingsModalController(): ChangeSettingsModalController {
        if (!this.changeSettingsModalController) {
            const changeSettingsState = new ChangeSettingsModalState();
            const modalController = new ChangeSettingsModalController(
                changeSettingsState,
                this.tsAppUserService as any,
                undefined
            );
            runInAction(() => {
                this.changeSettingsModalController = modalController;
            });
            return modalController;
        }
        return this.changeSettingsModalController;
    }

    private _getChangeAccountModalController(): ChangeAccountModalController | undefined {
        if (!this.changeAccountModalController && this.currentUser?.parentSubUserId) {
            const changeAccountState = new ChangeAccountModalState();
            runInAction(() => {
                this.changeAccountModalController = new ChangeAccountModalController(
                    changeAccountState,
                    this.authService,
                    this.appState
                );
            });
        }
        return this.changeAccountModalController;
    }

    async handleSettingsClick(): Promise<void> {
        try {
            const modalController = this._getChangeSettingsModalController();
            const updatedUser = await modalController.open({
                currentUser: this.currentUser,
                accountData: this.accountData,
                passwordOnly: false
            });
            if (updatedUser && this.currentUser) {
                Object.assign(this.currentUser, updatedUser);
            }
        } catch (err: any) {
            // User cancelled or error occurred
        }
    }

    handleLogoutClick(): void {
        const accountId = this.accountData?._id;
        const userId = this.currentUser?._id;
        const returnURL = window.location.origin + window.location.pathname;

        this.authService
            .logout(accountId, userId, returnURL)
            .then((res: any) => {
                if (res?.data) {
                    window.location.href = res.data;
                } else {
                    window.location.href = '/';
                }
            })
            .catch(() => {
                window.location.href = '/';
            });
    }

    handleAdminClick(): void {
        this.redirectionService.goToAdmin().catch((err: any) => {
            console.error('Failed to redirect to admin:', err);
        });
    }

    async handleSwitchAccountClick(): Promise<void> {
        const modalController = this._getChangeAccountModalController();
        if (!modalController) {
            return;
        }
        try {
            await modalController.open({
                currentUser: this.currentUser,
                accountData: this.accountData
            });
            window.location.reload();
        } catch (err: any) {
            // User cancelled or error occurred
        }
    }
}
